import type * as React from 'react';

import {
    ActionableContainer,
    Description,
    FieldGroup,
    Group,
    Heading,
    Label,
    Panel,
    PanelBody,
    Switch,
} from '@gitbook/spine-react';

import { DocumentationLink } from '../../Link';
import type { SiteOnlyCustomizationProps } from '../types';

export function PageActionsSettings(props: SiteOnlyCustomizationProps): React.ReactElement {
    const { settings, setSettings } = props;
    const pageActionsEnabled =
        settings.pageActions.externalAI ||
        settings.pageActions.markdown ||
        settings.pageActions.mcp ||
        settings.pdf.enabled ||
        settings.git.showEditLink;

    return (
        <Panel>
            <ActionableContainer orientation="horizontal">
                <Heading>Page actions</Heading>
                <Switch
                    value={pageActionsEnabled}
                    onValueChange={(checked) => {
                        setSettings((prev) => ({
                            ...prev,
                            pageActions: {
                                ...prev.pageActions,
                                externalAI: checked,
                                markdown: checked,
                                mcp: checked,
                            },
                            pdf: { ...prev.pdf, enabled: checked },
                            git: { ...prev.git, showEditLink: checked },
                        }));
                    }}
                />
            </ActionableContainer>
            {pageActionsEnabled ? (
                <PanelBody>
                    <FieldGroup orientation="horizontal">
                        <Group>
                            <Label>Open in AI providers</Label>
                            <Description>
                                Display actions to open ChatGPT or Claude with the page content.
                            </Description>
                        </Group>
                        <Switch
                            value={settings.pageActions.externalAI}
                            onValueChange={(value) => {
                                setSettings((prev) => ({
                                    ...prev,
                                    pageActions: {
                                        ...prev.pageActions,
                                        externalAI: value,
                                    },
                                }));
                            }}
                        />
                    </FieldGroup>
                    <FieldGroup orientation="horizontal">
                        <Group>
                            <Label>Connect with MCP server</Label>
                            <Description>
                                Display actions to connect to this site using MCP.
                            </Description>
                        </Group>
                        <Switch
                            value={settings.pageActions.mcp}
                            onValueChange={(value) => {
                                setSettings((prev) => ({
                                    ...prev,
                                    pageActions: {
                                        ...prev.pageActions,
                                        mcp: value,
                                    },
                                }));
                            }}
                        />
                    </FieldGroup>
                    <FieldGroup orientation="horizontal">
                        <Group>
                            <Label>Copy/View as Markdown</Label>
                            <Description>
                                Display actions to copy or view the page as Markdown.
                            </Description>
                        </Group>
                        <Switch
                            value={settings.pageActions.markdown}
                            onValueChange={(value) => {
                                setSettings((prev) => ({
                                    ...prev,
                                    pageActions: {
                                        ...prev.pageActions,
                                        markdown: value,
                                    },
                                }));
                            }}
                        />
                    </FieldGroup>
                    <FieldGroup orientation="horizontal">
                        <Group>
                            <Label>Edit on Git</Label>
                            <Description>
                                Display a link to edit the content on GitHub/GitLab.
                                <br />
                                <span className="text-ui-small">
                                    Git Sync must also be{' '}
                                    <DocumentationLink path="/getting-started/git-sync/enabling-github-sync">
                                        enabled
                                    </DocumentationLink>{' '}
                                    for your content.
                                </span>
                            </Description>
                        </Group>
                        <Switch
                            value={settings.git.showEditLink}
                            onValueChange={(value) => {
                                setSettings((prev) => ({
                                    ...prev,
                                    git: { ...prev.git, showEditLink: value },
                                }));
                            }}
                        />
                    </FieldGroup>
                    <FieldGroup orientation="horizontal">
                        <Group>
                            <Label>Export as PDF</Label>
                            <Description>
                                Display an action to export the page as a PDF.
                            </Description>
                        </Group>
                        <Switch
                            value={settings.pdf.enabled}
                            onValueChange={(value) => {
                                setSettings((prev) => ({
                                    ...prev,
                                    pdf: { ...prev.pdf, enabled: value },
                                }));
                            }}
                        />
                    </FieldGroup>
                </PanelBody>
            ) : null}
        </Panel>
    );
}
